import {useRef, useState} from 'react'
import {Plus} from 'lucide-react'
import type {BoardColumn, Task, TaskStatus} from '../../types'
import {useApp} from '../../context/AppContext'
import KanbanCard from './KanbanCard'

interface KanbanColumnProps {
  column: BoardColumn
  tasks: Task[]
  totalCount?: number
  onAddTask: (status: TaskStatus) => void
}

export default function KanbanColumn({
  column,
  tasks,
  onAddTask,
  totalCount = tasks.length,
}: KanbanColumnProps) {
  const {apiMoveTask, state} = useApp()
  const [error, setError] = useState('')
  const [moving, setMoving] = useState(false)
  const movingRef = useRef(false)
  const isOverLimit = column.limit !== undefined && totalCount > column.limit

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    ;(e.currentTarget as HTMLElement).classList.add('drag-over')
  }

  const handleDragLeave = (e: React.DragEvent) => {
    ;(e.currentTarget as HTMLElement).classList.remove('drag-over')
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    ;(e.currentTarget as HTMLElement).classList.remove('drag-over')
    const taskId = e.dataTransfer.getData('taskId')
    const task = state.tasks.find((item) => item.id === taskId)
    if (!task || task.status === column.status || movingRef.current) return
    movingRef.current = true
    setMoving(true)
    setError('')
    try {
      await apiMoveTask(taskId, column.status)
    } catch {
      setError('Não foi possível mover a tarefa. Tente novamente.')
    } finally {
      movingRef.current = false
      setMoving(false)
    }
  }

  return (
    <div className='devops-kanban-column flex flex-col min-w-[280px] w-[280px] md:min-w-[300px] md:w-[300px] shrink-0'>
      <div className='flex items-center justify-between px-1 mb-3'>
        <div className='flex items-center gap-2'>
          <div className='w-2.5 h-2.5 rounded-full' style={{backgroundColor: column.color}} />
          <h3 className='text-sm font-semibold text-slate-700'>{column.title}</h3>
          <span
            className={`text-xs font-medium px-1.5 py-0.5 rounded-md ${
              isOverLimit ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'
            }`}
          >
            {tasks.length === totalCount ? totalCount : `${tasks.length} de ${totalCount}`}
            {column.limit !== undefined && ` · limite ${column.limit}`}
          </span>
        </div>
        <button
          onClick={() => onAddTask(column.status)}
          className='p-1 rounded-lg text-slate-400 hover:text-brand-500 hover:bg-brand-50 transition-colors'
          aria-label={`Adicionar tarefa em ${column.title}`}
        >
          <Plus size={16} />
        </button>
      </div>

      {isOverLimit && (
        <p className='mb-2 px-1 text-xs text-red-600'>
          Limite de trabalho excedido em {totalCount - (column.limit || 0)} tarefa(s).
        </p>
      )}
      {error && (
        <p role='alert' className='mb-2 rounded-lg bg-red-50 p-2 text-xs text-red-700'>
          {error}
        </p>
      )}
      {moving && (
        <p role='status' className='px-1 text-xs text-brand-600'>
          Movendo tarefa...
        </p>
      )}
      <div
        aria-busy={moving}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className='flex-1 space-y-2.5 p-1 rounded-xl min-h-[200px] transition-colors duration-200'
      >
        {tasks.map((task, index) => (
          <div
            key={task.id}
            className='animate-slide-in-up'
            style={{animationDelay: `${index * 30}ms`}}
          >
            <KanbanCard task={task} />
          </div>
        ))}
        {tasks.length === 0 && (
          <div className='flex items-center justify-center h-32 rounded-xl border border-dashed border-slate-300 bg-white/50 text-xs text-slate-400'>
            {totalCount > 0
              ? 'Nenhuma tarefa com estes filtros'
              : 'Arraste uma tarefa ou use o botão +'}
          </div>
        )}
      </div>
    </div>
  )
}
